/**
 * QAPanel — the in-game QA checklist view. State machine lives in
 * sessionReducer.ts, the three-state checkbox in QACheckbox.tsx,
 * import/export helpers in qaSessionIO.ts, and the template registry
 * in qaTemplates.ts (mechanical split from the former monolith).
 */

import React, { useReducer, useEffect, useRef, useMemo } from 'react';
import { storage } from '../../../services/StorageService';
import { TEMPLATES } from './qaTemplates';
import type { QACategory } from './qaTypes';
import { EventBus } from '../../../../shared/EventBus';
import { sessionReducer, loadOrCreateSession } from './sessionReducer';
import { QACheckbox } from './QACheckbox';
import { downloadSessionJSON, parseSessionFile, buildSummary } from './qaSessionIO';

// ── Main Panel ───────────────────────────────────────────

export const QAPanelContent: React.FC = () => {
  const [templateIdx, setTemplateIdx] = React.useState(0);
  const [session, dispatch] = useReducer(sessionReducer, null, () => loadOrCreateSession(TEMPLATES[0].build));
  const [categoryIdx, setCategoryIdx] = React.useState(0);
  const [expandedNotes, setExpandedNotes] = React.useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const switchTemplate = (idx: number) => {
    setTemplateIdx(idx);
    setCategoryIdx(0);
    dispatch({ type: 'LOAD', session: loadOrCreateSession(TEMPLATES[idx].build) });
  };

  // Auto-save on every change
  useEffect(() => {
    storage.setObjectRaw(`qa_session_${session.templateId}`, session);
  }, [session]);

  const cat: QACategory = session.categories[categoryIdx] || session.categories[0];

  // Progress stats
  const stats = useMemo(() => {
    let total = 0, pass = 0, fail = 0;
    for (const c of session.categories) {
      for (const item of c.items) {
        total++;
        if (item.status === 'pass') pass++;
        else if (item.status === 'fail') fail++;
      }
    }
    return { total, pass, fail, unchecked: total - pass - fail };
  }, [session]);

  const catStats = useMemo(() => {
    let pass = 0, fail = 0;
    for (const item of cat.items) {
      if (item.status === 'pass') pass++;
      else if (item.status === 'fail') fail++;
    }
    return { total: cat.items.length, pass, fail };
  }, [cat]);

  // ── Actions ──

  const downloadJSON = () => downloadSessionJSON(session);

  const loadJSON = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    parseSessionFile(file).then(loaded => {
      if (loaded) dispatch({ type: 'LOAD', session: loaded });
    });
    e.target.value = '';
  };

  const exportSummary = () => {
    const summary = buildSummary(session, stats);
    navigator.clipboard.writeText(JSON.stringify(summary, null, 2)).then(
      () => alert('QA summary copied to clipboard!'),
      () => alert('Failed to copy — check console')
    );
  };

  // ── Styles ──

  const btnStyle: React.CSSProperties = {
    padding: '5px 10px',
    fontSize: 13,
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: 4,
    background: 'rgba(255,255,255,0.08)',
    color: '#ccc',
    cursor: 'pointer',
  };

  const progressPct = stats.total > 0 ? ((stats.pass + stats.fail) / stats.total) * 100 : 0;

  return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 8, fontSize: 15 }}>

        {/* ── Progress header ── */}
        <div style={{ padding: '0 2px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 13, color: '#aaa' }}>
            <span>{stats.pass + stats.fail}/{stats.total} checked</span>
            <span style={{ color: '#2ecc71' }}>{stats.pass} pass</span>
            <span style={{ color: '#e74c3c' }}>{stats.fail} fail</span>
            <span>{stats.unchecked} left</span>
          </div>
          <div style={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: `${progressPct}%`,
              background: stats.fail > 0 ? 'linear-gradient(90deg, #2ecc71, #e74c3c)' : '#2ecc71',
              transition: 'width 0.3s',
            }} />
          </div>
        </div>

        {/* ── Template selector ── */}
        <div style={{ display: 'flex', gap: 4, padding: '2px 0' }}>
          {TEMPLATES.map((t, idx) => (
            <button
              key={t.label}
              onClick={() => switchTemplate(idx)}
              style={{
                ...btnStyle,
                flex: 1,
                fontSize: 12,
                padding: '3px 6px',
                background: idx === templateIdx ? 'rgba(46,204,113,0.3)' : 'rgba(255,255,255,0.05)',
                borderColor: idx === templateIdx ? '#2ecc71' : 'rgba(255,255,255,0.15)',
                color: idx === templateIdx ? '#2ecc71' : '#888',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Class switcher (visible for ability-related templates) ── */}
        {session.categories.some(c => c.id.startsWith('ability_qa_')) && (
          <div style={{ display: 'flex', gap: 4, padding: '2px 0' }}>
            {(['warrior', 'mage', 'hunter', 'paladin', 'rogue', 'shaman', 'priest', 'warlock', 'druid'] as const).map(cls => {
              const icons: Record<string, string> = { warrior: '\u2694\uFE0F', mage: '\uD83E\uDDD9', hunter: '\uD83C\uDFF9', paladin: '\uD83D\uDEE1\uFE0F', rogue: '\uD83D\uDDE1\uFE0F', shaman: '\uD83C\uDF29\uFE0F', priest: '\u2728', warlock: '\uD83D\uDE08', druid: '\uD83C\uDF3F' };
              const isActive = cat.id === `ability_qa_${cls}`;
              return (
                <button
                  key={cls}
                  onClick={() => {
                    const idx = session.categories.findIndex(c => c.id === `ability_qa_${cls}`);
                    if (idx >= 0) setCategoryIdx(idx);
                    EventBus.getInstance().emit('dev:action', { type: 'set_class', data: cls });
                  }}
                  style={{
                    ...btnStyle,
                    flex: 1,
                    fontSize: 13,
                    padding: '4px 2px',
                    background: isActive ? 'rgba(52,152,219,0.3)' : 'rgba(255,255,255,0.05)',
                    borderColor: isActive ? '#3498db' : 'rgba(255,255,255,0.15)',
                    color: isActive ? '#3498db' : '#888',
                  }}
                >
                  {icons[cls]} {cls.charAt(0).toUpperCase() + cls.slice(1)}
                </button>
              );
            })}
          </div>
        )}

        {/* ── Category nav ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0' }}>
          <button
            style={{ ...btnStyle, padding: '4px 8px' }}
            onClick={() => setCategoryIdx(i => (i - 1 + session.categories.length) % session.categories.length)}
          >
            &#9664;
          </button>
          <div style={{ fontWeight: 'bold', color: '#e0e0ff' }}>
            {cat.icon} {cat.label}
            <span style={{ fontWeight: 'normal', color: '#888', marginLeft: 8, fontSize: 13 }}>
              {categoryIdx + 1}/{session.categories.length}
            </span>
            <span style={{ marginLeft: 8, fontSize: 13, color: catStats.pass === catStats.total ? '#2ecc71' : '#aaa' }}>
              {catStats.pass}/{catStats.total} OK
            </span>
          </div>
          <button
            style={{ ...btnStyle, padding: '4px 8px' }}
            onClick={() => setCategoryIdx(i => (i + 1) % session.categories.length)}
          >
            &#9654;
          </button>
        </div>

        {/* ── Item list ── */}
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {cat.items.map(item => (
            <div key={item.id}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '4px 6px',
                  borderRadius: 4,
                  background: item.status === 'fail' ? 'rgba(231,76,60,0.1)' : 'transparent',
                }}
              >
                <QACheckbox
                  status={item.status}
                  onClick={() => dispatch({ type: 'TOGGLE_ITEM', categoryId: cat.id, itemId: item.id })}
                />
                <span
                  style={{
                    flex: 1,
                    color: item.status === 'pass' ? '#2ecc71' : item.status === 'fail' ? '#e74c3c' : '#ccc',
                    textDecoration: item.status === 'pass' ? 'line-through' : 'none',
                    opacity: item.status === 'pass' ? 0.7 : 1,
                    cursor: 'default',
                  }}
                >
                  {item.label}
                </span>
                <button
                  style={{
                    background: 'none',
                    border: 'none',
                    color: item.notes ? '#f1c40f' : '#555',
                    cursor: 'pointer',
                    fontSize: 15,
                    padding: '0 4px',
                  }}
                  title={item.notes || 'Add note'}
                  onClick={() => setExpandedNotes(expandedNotes === item.id ? null : item.id)}
                >
                  {item.notes ? '\uD83D\uDCDD' : '\u270F\uFE0F'}
                </button>
              </div>
              {expandedNotes === item.id && (
                <textarea
                  value={item.notes}
                  onChange={e => dispatch({ type: 'SET_ITEM_NOTES', categoryId: cat.id, itemId: item.id, notes: e.target.value })}
                  placeholder="Notes..."
                  style={{
                    width: '100%',
                    minHeight: 40,
                    marginTop: 2,
                    marginBottom: 4,
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: 4,
                    color: '#ddd',
                    fontSize: 13,
                    padding: 6,
                    resize: 'vertical',
                    fontFamily: 'inherit',
                  }}
                  autoFocus
                />
              )}
            </div>
          ))}
        </div>

        {/* ── Footer ── */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 12, color: '#888', fontWeight: 'bold' }}>Notes</div>
          <textarea
            value={session.globalNotes}
            onChange={e => dispatch({ type: 'SET_GLOBAL_NOTES', notes: e.target.value })}
            placeholder="Type anything here — bugs, observations, things to fix..."
            style={{
              width: '100%',
              minHeight: 60,
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: 4,
              color: '#ddd',
              fontSize: 14,
              padding: 8,
              resize: 'vertical',
              fontFamily: 'inherit',
            }}
          />

          {/* Sign-off */}
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              fontWeight: 'bold',
              color: session.overallSignOff ? '#2ecc71' : '#f1c40f',
              fontSize: 14,
            }}
          >
            <QACheckbox
              status={session.overallSignOff ? 'pass' : 'unchecked'}
              onClick={() => dispatch({ type: 'TOGGLE_SIGN_OFF' })}
            />
            Overall Sign-Off
          </label>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <button style={btnStyle} onClick={downloadJSON}>Download JSON</button>
            <button style={btnStyle} onClick={() => fileInputRef.current?.click()}>Load JSON</button>
            <button style={btnStyle} onClick={exportSummary}>Export Summary</button>
            <button
              style={{ ...btnStyle, borderColor: 'rgba(231,76,60,0.4)', color: '#e74c3c' }}
              onClick={() => { if (confirm('Reset all checks?')) dispatch({ type: 'RESET' }); }}
            >
              Reset
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              style={{ display: 'none' }}
              onChange={loadJSON}
            />
          </div>
        </div>
      </div>
  );
};
